import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import AssetDownload from '@comp/product/asset-download'
import FinalCta from '@comp/home/final-cta'

const whitepaper = {
  title: {
    en: 'The CLM whitepaper, with live product screenshots.',
    tr: 'CLM whitepaper’ı, canlı ürün ekran görüntüleriyle.',
  },
  copy: {
    en: 'Why certificate lifetimes are collapsing to 47 days, what breaks, and how PAM-native CLM answers it: 11 pages with the discovery, PQC, and CT-monitoring screens captured from the running product.',
    tr: 'Sertifika ömürleri neden 47 güne iniyor, neler kırılıyor ve PAM-yerlisi CLM buna nasıl cevap veriyor: keşif, PQC ve CT izleme ekranları çalışan üründen alınmış 11 sayfa.',
  },
}

const pillars: Pillar[] = [
  {
    title: 'Discover every certificate you own',
    description:
      'TLS-handshake scanning across your network plus file-system and Windows certificate-store discovery over the Monopam Gateway: no agents to deploy.',
    bullets: [
      'Network scans by IP range, subnet, CIDR, and port',
      'File-system and Windows cert-store discovery via SSH / WinRM',
      'Scheduled re-scans keep the inventory current',
      'Thumbprint-deduplicated inventory with per-endpoint locations',
    ],
  },
  {
    title: 'Issue from the CA you already trust',
    description:
      'ACME for public certificates, Microsoft AD CS for the enterprise, a built-in private CA for everything internal, every request behind an approval workflow.',
    bullets: [
      "ACME with Let's Encrypt or any ACMEv2 endpoint (HTTP-01, DNS-01)",
      'Microsoft AD CS integration and CSR generation',
      'Built-in private CA: root, intermediate, and leaf',
      'SCEP / EST device enrollment',
    ],
  },
  {
    title: 'Renew and deploy without agents',
    description:
      'Renewal policies renew certificates before they expire and push them to the servers that use them, closing the loop that spreadsheets never could.',
    bullets: [
      'Automatic renewal policies with archive-on-replace',
      'Linux deploy: PEM write + nginx / Apache reload',
      'Windows deploy: PFX import, IIS binding, service restart',
      'Renew-then-deploy as one closed loop',
    ],
  },
  {
    title: 'See risk before it becomes an outage',
    description:
      'Expiry alerting is table stakes. Monopam also probes TLS configuration, watches CT logs, and reports on your post-quantum readiness.',
    bullets: [
      'Expiry digests at 30 / 14 / 7 / 1 days',
      'TLS protocol probing from SSLv3 through TLS 1.3',
      'Certificate Transparency log monitoring with alerts',
      'Crypto-agility report: weak keys, SHA-1, quantum-vulnerable inventory',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Scan',
    description:
      'Point Monopam at your IP ranges and servers. Discovery builds a deduplicated inventory of every certificate, everywhere it lives.',
  },
  {
    title: 'Set policy',
    description:
      'Map certificates to CAs, define renewal windows and approval gates, and choose deployment targets per certificate.',
  },
  {
    title: 'Automate',
    description:
      'Monopam renews, deploys, reloads, and alerts, and your team stops chasing expiry dates in spreadsheets.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Use case: machine identity',
    description:
      'Certificates, keys, and service accounts as one discipline.',
    href: '/solutions/machine-identity',
  },
  {
    label: 'Blog: 47-day certificates',
    description:
      'Why TLS lifetimes are shrinking, and what breaks when they do.',
    href: '/blog/tls-certificate-lifetimes-are-shrinking-to-47-days',
  },
  {
    label: 'What is CLM?',
    description:
      'Certificate lifecycle management, explained in the glossary.',
    href: '/glossary/certificate-lifecycle-management',
  },
]

export default function CertificateManagerPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Certificate Lifecycle Management (CLM)"
        description="Agentless certificate lifecycle management inside your PAM: discover every TLS certificate, issue via ACME / AD CS / private CA, renew and deploy automatically, and report on post-quantum readiness."
        path="/pam/certificate-manager"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Certificate Manager"
          title={
            <>
              An expired certificate
              <br />
              is an outage. Never again.
            </>
          }
          body="Public TLS certificate lifetimes are shrinking toward 47 days; manual tracking is over. Monopam builds certificate lifecycle management (CLM) into your PAM: it discovers every certificate you own, issues from ACME, AD CS, or a built-in CA, and renews and deploys automatically. Agentless, approval-gated, audit-ready."
          badges={[
            'Agentless discovery & deployment',
            'ACME + AD CS + built-in CA',
            'Post-quantum readiness reports',
          ]}
        />
        <SolutionPillars
          title={<>Machine identities, managed like privileged ones.</>}
          body="The platform that brokers privileged access already reaches every server you run; Monopam uses that same agentless channel to manage the certificates on them."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From spreadsheet to closed loop in three steps.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <AssetDownload
          asset="clm-whitepaper"
          title={whitepaper.title}
          copy={whitepaper.copy}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}
